import {useCallback, useState} from 'react';

import FolderDAOCache from 'file-browser/FolderDAOCache';
import notifications from 'notifications';
import {IGNORE_FIELD_ID} from 'spreadsheet-import-wizard/shared/ColumnMapper';

import type {ColumnMapping} from './moleculeImportDAO';
import {bulkImportMoleculesFromSpreadsheet} from './moleculeImportDAO';
import type {ModalAction, ModalState} from './moleculeImportReducer';

const BUILT_IN_SENTINELS: Record<string, string> = {
  __molecule_name__: 'NAME',
  __molecule_structure_smiles__: 'SMILES',
  __molecule_structure_molfile__: 'MOLFILE',
  __molecule_alias__: 'ALIAS',
};

export default function useSubmitImport(
  state: ModalState,
  dispatch: React.Dispatch<ModalAction>,
  close: () => void
): {handleSubmit: () => Promise<void>; isSubmitting: boolean} {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (!state.s3Url || !state.registrationConfig || !state.schemaId) {
      return;
    }
    setIsSubmitting(true);
    dispatch({type: 'GO_TO_STEP', payload: {step: 'SUBMITTING'}});

    const columnMappings: Array<ColumnMapping> = [];
    for (const [columnName, fieldId] of Object.entries(state.columnMappings)) {
      if (fieldId === IGNORE_FIELD_ID) {
        continue;
      }
      const role = BUILT_IN_SENTINELS[fieldId];
      if (role) {
        columnMappings.push({type: 'builtIn', columnName, role});
      } else {
        const expandedKeys = state.fieldExpansion[fieldId] ?? [fieldId];
        for (const key of expandedKeys) {
          columnMappings.push({type: 'schemaField', columnName, schemaFieldKey: key});
        }
      }
    }

    const folderId = state.registrationConfig.folderId;

    try {
      await bulkImportMoleculesFromSpreadsheet({
        s3Url: state.s3Url,
        columnMappings,
        registrationConfig: {
          registryId: state.registrationConfig.registryId,
          namingStrategy: state.registrationConfig.namingStrategy,
          folderId,
          schemaId: state.schemaId,
          compoundSchemaId: state.compoundSchemaId,
        },
      });

      close();

      notifications.info("Your molecule import started. You'll receive an email when it completes.");
      FolderDAOCache.invalidateFolderContents(folderId);
    } catch (error: unknown) {
      setIsSubmitting(false);
      const taskError = error as {isBenchlingTaskError?: boolean; responseJSON?: {message?: string}};
      if (taskError.isBenchlingTaskError || taskError.responseJSON) {
        const message =
          (taskError.responseJSON as {message?: string})?.message ??
          'An unexpected error occurred while submitting the import. Please try again.';
        dispatch({type: 'SET_ERROR_MESSAGE', payload: {errorMessage: message}});
        dispatch({type: 'GO_TO_STEP', payload: {step: 'FINALIZE_IMPORT'}});
      } else {
        throw error;
      }
    }
  }, [state, dispatch, close]);

  return {handleSubmit, isSubmitting};
}
